'use client';

import { createContext, useContext, useEffect, useRef, useCallback, type ReactNode } from 'react';

export interface LogEntry {
  timestamp: string;
  level: 'log' | 'warn' | 'error';
  message: string;
  stack?: string;
}

interface ConsoleLogContextValue {
  getRecentLogs: () => LogEntry[];
}

const ConsoleLogContext = createContext<ConsoleLogContextValue>({
  getRecentLogs: () => [],
});

export function useConsoleLogs() {
  return useContext(ConsoleLogContext);
}

const MAX_ENTRIES = 50;
const MAX_MESSAGE_LENGTH = 500;

function truncate(str: string): string {
  return str.length > MAX_MESSAGE_LENGTH ? str.slice(0, MAX_MESSAGE_LENGTH) + '…' : str;
}

function argsToString(args: unknown[]): string {
  return args
    .map((a) => {
      if (typeof a === 'string') return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

export function ConsoleLogProvider({ children }: { children: ReactNode }) {
  const bufferRef = useRef<LogEntry[]>([]);

  const getRecentLogs = useCallback(() => [...bufferRef.current], []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const buffer = bufferRef.current;

    const push = (entry: LogEntry) => {
      buffer.push(entry);
      if (buffer.length > MAX_ENTRIES) {
        buffer.shift();
      }
    };

    // 원본 console 메서드 보관
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const SUPPRESSED_PATTERNS = [
      'A tree hydrated but some attributes',
      'did not match. Server:',
      'Hydration failed because',
    ];

    const wrap = (level: LogEntry['level'], original: (...args: unknown[]) => void) => {
      return (...args: unknown[]) => {
        const msg = argsToString(args);
        if (SUPPRESSED_PATTERNS.some((p) => msg.includes(p))) return;
        push({
          timestamp: new Date().toISOString(),
          level,
          message: truncate(msg),
        });
        original.apply(console, args);
      };
    };

    console.log = wrap('log', originalLog);
    console.warn = wrap('warn', originalWarn);
    console.error = wrap('error', originalError);

    // 전역 에러 캡처
    const handleError = (event: ErrorEvent) => {
      push({
        timestamp: new Date().toISOString(),
        level: 'error',
        message: truncate(event.message || 'Unknown error'),
        stack: event.error?.stack ? truncate(event.error.stack) : undefined,
      });
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const msg = event.reason?.message || String(event.reason);
      push({
        timestamp: new Date().toISOString(),
        level: 'error',
        message: truncate(`Unhandled rejection: ${msg}`),
        stack: event.reason?.stack ? truncate(event.reason.stack) : undefined,
      });
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return (
    <ConsoleLogContext.Provider value={{ getRecentLogs }}>
      {children}
    </ConsoleLogContext.Provider>
  );
}
